// Admin Messaging Management v2 — /api/admin/v2/{messages,message-conversations,message-reports,message-broadcast,message-bans}

export type MessagePriority = 'low' | 'normal' | 'high' | 'urgent' | string;
export type MessageStatus = 'sent' | 'delivered' | 'read' | 'deleted';
export type SenderKind = 'user' | 'org' | 'platform';
export type RecipientKind = 'user' | 'org';
export type ReportStatus = 'pending' | 'reviewing' | 'actioned' | 'dismissed';
export type ReportResolveAction = 'review' | 'dismiss' | 'delete' | 'redact' | 'resolve';
export type BanSubjectType = 'user' | 'org';
export type BanScope = 'all' | 'outbound' | 'dm' | string;

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  has_more?: boolean;
}

/** Echoed by list endpoints after applying sort/order. */
export interface AppliedSort {
  by: string;
  order: 'asc' | 'desc' | string;
}

export type SortOrder = 'asc' | 'desc';

export interface MessageReaction {
  uid: number;
  emoji: string;
  at?: string;
}

/** Thin party summary attached by admin messaging enrichment. */
export interface MessagingPartySummary {
  uid?: number;
  oid?: number;
  name?: string | null;
  preferred_name?: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  ffid?: string | null;
  photo?: unknown;
  photo_url?: string | null;
}

export interface MessageAttachment {
  url?: string;
  eid?: number | string | null;
  /** Off-platform "contact the lister" lead */
  kind?: string;
  display_only?: boolean;
  visitor_email?: string;
  visitor_phone?: string;
  visitor_first?: string;
  visitor_last?: string;
  [key: string]: unknown;
}

export interface AdminMessage {
  id: string;
  hash?: string | null;
  message?: string | null;
  attachment?: MessageAttachment | null;
  /** API may return numeric or string IDs depending on writer. */
  sender_uid?: number | string | null;
  recipient_uid?: number | string | null;
  sender_oid?: number | string | null;
  recipient_oid?: number | string | null;
  entity?: number | string | null;
  conversation_key?: string | null;
  seq?: number | null;
  priority?: MessagePriority;
  is_read?: boolean;
  is_delivered?: boolean;
  is_deleted?: boolean;
  is_automated?: boolean;
  is_platform?: boolean;
  redacted?: boolean;
  reply_to_hash?: string | null;
  reactions?: MessageReaction[];
  /** Epoch seconds (API-normalized). Defensive: also accept legacy ms. */
  timestamp?: number;
  created_at?: number;
  updated_at?: number | null;
  sender_kind?: SenderKind;
  recipient_kind?: RecipientKind;
  status?: MessageStatus | string;
  sender_user_summary?: MessagingPartySummary | null;
  recipient_user_summary?: MessagingPartySummary | null;
  sender_org_summary?: MessagingPartySummary | null;
  recipient_org_summary?: MessagingPartySummary | null;
  [key: string]: unknown;
}

export interface MessageOverview {
  messages_total?: number;
  messages_deleted?: number;
  conversations_total?: number;
  reports_pending?: number;
  bans_active?: number;
  [key: string]: unknown;
}

export interface ConversationRegistry {
  conversation_key: string;
  participants?: unknown;
  last_message_ts?: number | null;
  last_seq?: number | null;
  last_hash?: string | null;
  created_ts?: number | null;
  /** Set while the thread is blocked for a policy violation. */
  blocked?: { reason?: string | null; by?: number | null; at?: number | null } | null;
  user_summaries?: Record<string, MessagingPartySummary>;
  org_summaries?: Record<string, MessagingPartySummary>;
  [key: string]: unknown;
}

export interface ConversationDetail {
  registry: ConversationRegistry;
  counts?: {
    total?: number;
    active?: number;
    deleted?: number;
    unread?: number;
  };
  user_summaries?: Record<string, MessagingPartySummary>;
  org_summaries?: Record<string, MessagingPartySummary>;
}

export interface MessageReport {
  rid: string;
  status?: ReportStatus | string;
  category?: string | null;
  reason?: string | null;
  conversation_key?: string | null;
  reporter_uid?: number | null;
  reporter_oid?: number | null;
  resolved_by?: number | null;
  action_taken?: string | null;
  message_ref?: string | null;
  message_snapshot?: unknown;
  notes?: string | null;
  side_effect?: string | null;
  created_at?: number;
  updated_at?: number;
  resolved_at?: number | null;
  reporter_summary?: MessagingPartySummary | null;
  reporter_org_summary?: MessagingPartySummary | null;
  resolved_by_summary?: MessagingPartySummary | null;
  [key: string]: unknown;
}

export interface MessagingBan {
  banid: string;
  subject_type?: BanSubjectType | string;
  subject_id?: number;
  scope?: BanScope;
  reason?: string;
  active?: boolean;
  expires_at?: number | null;
  created_at?: number;
  created_by?: number | null;
  lifted_at?: number | null;
  lifted_by?: number | null;
  subject_summary?: MessagingPartySummary | null;
  created_by_summary?: MessagingPartySummary | null;
  lifted_by_summary?: MessagingPartySummary | null;
  [key: string]: unknown;
}

export interface MessageAuditEntry {
  action?: string;
  actor_uid?: number;
  target_type?: string;
  target_id?: string;
  meta?: unknown;
  created_at?: number;
  [key: string]: unknown;
}

export interface BroadcastSendResult {
  success?: boolean;
  id?: string;
  hash?: string;
  conversation_key?: string;
  recipient_uid?: number | null;
  recipient_oid?: number | null;
  [key: string]: unknown;
}

export interface BroadcastBulkResult {
  success?: boolean;
  sent?: number;
  [key: string]: unknown;
}

export interface MessagesListResponse {
  messages: AdminMessage[];
  pagination: Pagination;
  sort?: AppliedSort;
}

export interface ConversationsListResponse {
  conversations: ConversationRegistry[];
  pagination: Pagination;
  sort?: AppliedSort;
}

export interface ReportsListResponse {
  reports: MessageReport[];
  pagination: Pagination;
  sort?: AppliedSort;
}

export interface BansListResponse {
  bans: MessagingBan[];
  pagination: Pagination;
  sort?: AppliedSort;
}

export interface AuditListResponse {
  audit?: MessageAuditEntry[];
  entries?: MessageAuditEntry[];
  pagination?: Pagination;
  [key: string]: unknown;
}

export interface MessageListParams {
  uid?: number;
  oid?: number;
  sender_uid?: number;
  recipient_uid?: number;
  sender_oid?: number;
  recipient_oid?: number;
  sender_kind?: SenderKind | string;
  entity?: number;
  conversation_key?: string;
  hash?: string;
  reply_to_hash?: string;
  q?: string;
  attachment_kind?: string;
  has_attachment?: boolean | 0 | 1 | string;
  has_reactions?: boolean | 0 | 1 | string;
  is_reply?: boolean | 0 | 1 | string;
  status?: MessageStatus | string;
  priority?: string;
  is_read?: boolean | 0 | 1 | string;
  is_delivered?: boolean | 0 | 1 | string;
  is_deleted?: boolean | 0 | 1 | string;
  is_automated?: boolean | 0 | 1 | string;
  is_platform?: boolean | 0 | 1 | string;
  redacted?: boolean | 0 | 1 | string;
  date_from?: number;
  date_to?: number;
  updated_from?: number;
  updated_to?: number;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

/** GET /messages/thread — full back-and-forth by parties (works when conversation_key is null). */
export interface MessageThreadParams {
  a_uid?: number;
  a_oid?: number;
  b_uid?: number;
  b_oid?: number;
  entity?: number;
  include_deleted?: boolean | 0 | 1 | string;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

export interface MessageThreadParty {
  uid?: number | null;
  oid?: number | null;
  user_summary?: MessagingPartySummary | null;
  org_summary?: MessagingPartySummary | null;
}

export interface MessageThreadResponse {
  party_a?: MessageThreadParty;
  party_b?: MessageThreadParty;
  entity?: number | null;
  messages: AdminMessage[];
  pagination: Pagination;
  sort?: AppliedSort;
}

export interface ConversationListParams {
  uid?: number;
  oid?: number;
  conversation_key?: string;
  key_prefix?: string;
  date_from?: number;
  date_to?: number;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

export interface ConversationMessagesParams {
  include_deleted?: boolean | 0 | 1 | string;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

export interface ReportListParams {
  status?: ReportStatus | string;
  category?: string;
  action_taken?: string;
  conversation_key?: string;
  reporter_uid?: number;
  resolved_by?: number;
  message_ref?: string;
  date_from?: number;
  date_to?: number;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

export interface BanListParams {
  subject_type?: BanSubjectType | string;
  subject_id?: number;
  active?: boolean | 0 | 1 | string;
  expired?: boolean | 0 | 1 | string;
  scope?: BanScope | string;
  created_by?: number;
  sort?: string;
  order?: SortOrder | string;
  limit?: number;
  offset?: number;
}

export interface AuditListParams {
  action?: string;
  actor_uid?: number;
  target_type?: string;
  limit?: number;
  offset?: number;
}

export interface EditMessageFlagsPayload {
  is_read?: boolean;
  is_delivered?: boolean;
  priority?: string;
}

export interface FlagReportPayload {
  message_ref: string;
  reason?: string;
  category?: string;
  reporter_uid?: number;
}

export interface BroadcastSendPayload {
  uid?: number;
  oid?: number;
  message: string;
  entity?: number;
  priority?: string;
  attachment?: MessageAttachment;
}

export interface BroadcastBulkPayload {
  message: string;
  targets: Array<{ uid?: number; oid?: number; entity?: number }>;
  priority?: string;
}

export interface CreateBanPayload {
  subject_type: BanSubjectType;
  subject_id: number;
  scope?: BanScope;
  reason: string;
  expires_at?: number | null;
}

export interface SetBlockPayload {
  user_uid: number;
  blocked_uid: number;
  block?: boolean;
}

export const formatEpoch = (ts?: number | null): string => {
  if (ts == null || !Number.isFinite(Number(ts))) return '—';
  try {
    const n = Number(ts);
    // API normalizes to seconds; tolerate accidental ms leftovers.
    const ms = n > 1e12 ? n : n * 1000;
    return new Date(ms).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(ts);
  }
};

export const formatRelativeEpoch = (ts?: number | null): string => {
  if (ts == null || !Number.isFinite(Number(ts))) return '—';
  const n = Number(ts);
  const ms = n > 1e12 ? n : n * 1000;
  const diff = Date.now() - ms;
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatEpoch(ts);
};

export const coerceId = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const partyDisplayName = (
  summary?: MessagingPartySummary | null,
  fallback = '—',
): string => {
  if (!summary) return fallback;
  const composed = [summary.first_name, summary.last_name].filter(Boolean).join(' ').trim();
  return (
    summary.name ||
    summary.preferred_name ||
    composed ||
    summary.username ||
    fallback
  );
};

export const partyPhotoUrl = (summary?: MessagingPartySummary | null): string | null => {
  if (!summary) return null;
  if (typeof summary.photo_url === 'string' && summary.photo_url.trim()) {
    return summary.photo_url.trim();
  }
  const photo = summary.photo as
    | string
    | { public_url?: string; public_url_uc?: string; url?: string; path?: string }
    | null
    | undefined;
  if (!photo) return null;
  if (typeof photo === 'string') return photo;
  return photo.public_url || photo.public_url_uc || photo.url || photo.path || null;
};

export const isInquiryMessage = (msg?: AdminMessage | null): boolean =>
  !!msg && (msg.attachment?.kind === 'inquiry' || msg.attachment?.display_only === true);

export const inquiryVisitorName = (msg: AdminMessage): string | null => {
  const first = msg.attachment?.visitor_first;
  const last = msg.attachment?.visitor_last;
  const name = [first, last].filter(Boolean).join(' ').trim();
  return name || msg.attachment?.visitor_email || null;
};

export const partyLabel = (msg: AdminMessage): string => {
  if (isInquiryMessage(msg)) {
    const visitor = inquiryVisitorName(msg) || 'Visitor';
    const recipient =
      msg.recipient_uid != null && String(msg.recipient_uid) !== ''
        ? `uid ${msg.recipient_uid}`
        : msg.recipient_oid != null && String(msg.recipient_oid) !== ''
          ? `oid ${msg.recipient_oid}`
          : 'lister';
    return `Visitor ${visitor} → ${recipient}`;
  }
  const sender =
    msg.sender_kind === 'platform' || msg.is_platform
      ? 'FeelyFeely'
      : msg.sender_uid != null && String(msg.sender_uid) !== ''
        ? `uid ${msg.sender_uid}`
        : msg.sender_oid != null && String(msg.sender_oid) !== ''
          ? `oid ${msg.sender_oid}`
          : '—';
  const recipient =
    msg.recipient_uid != null && String(msg.recipient_uid) !== ''
      ? `uid ${msg.recipient_uid}`
      : msg.recipient_oid != null && String(msg.recipient_oid) !== ''
        ? `oid ${msg.recipient_oid}`
        : '—';
  return `${sender} → ${recipient}`;
};

export const conversationTitle = (key?: string | null): string => {
  if (!key) return 'Unknown thread';
  const parts = key.split(':');
  const kind = parts[0];
  if (kind === 'userpair' && parts.length >= 3) return `Users ${parts[1]} ↔ ${parts[2]}`;
  if (kind === 'orguser' && parts.length >= 3) return `Org ${parts[1]} ↔ User ${parts[2]}`;
  if (kind === 'orgpair' && parts.length >= 3) return `Orgs ${parts[1]} ↔ ${parts[2]}`;
  if (kind === 'platform' && parts[1] === 'user') return `FeelyFeely → User ${parts[2]}`;
  if (kind === 'platform' && parts[1] === 'org') return `FeelyFeely → Org ${parts[2]}`;
  return key;
};

export const entityFromKey = (key?: string | null): string | null => {
  if (!key) return null;
  const match = key.match(/:eid:([^:]+)$/);
  if (!match || match[1] === 'none') return null;
  return match[1];
};

/** Prefer top-level entity, then attachment.eid, then conversation_key eid. */
export const messageEntity = (msg: AdminMessage): string | number | null => {
  if (msg.entity != null && String(msg.entity) !== '') return msg.entity;
  const attEid = msg.attachment?.eid;
  if (attEid != null && String(attEid) !== '') return attEid;
  return entityFromKey(msg.conversation_key);
};

/**
 * Build /messages/thread params from a message row.
 * Prefer this over conversation_key — keys are often null on legacy/inquiry rows.
 */
export const threadParamsFromMessage = (msg: AdminMessage): MessageThreadParams | null => {
  const aUid = coerceId(msg.sender_uid);
  const aOid = coerceId(msg.sender_oid);
  const bUid = coerceId(msg.recipient_uid);
  const bOid = coerceId(msg.recipient_oid);
  if ((aUid == null && aOid == null) || (bUid == null && bOid == null)) return null;

  const entity = coerceId(messageEntity(msg) ?? undefined) ?? undefined;
  return {
    ...(aUid != null ? { a_uid: aUid } : { a_oid: aOid! }),
    ...(bUid != null ? { b_uid: bUid } : { b_oid: bOid! }),
    ...(entity != null ? { entity } : {}),
  };
};

export const STATUS_BADGE: Record<string, string> = {
  sent: 'bg-gray-100 text-gray-700',
  delivered: 'bg-blue-50 text-blue-700',
  read: 'bg-emerald-50 text-emerald-700',
  deleted: 'bg-red-50 text-red-700',
  pending: 'bg-amber-50 text-amber-800',
  reviewing: 'bg-blue-50 text-blue-700',
  actioned: 'bg-emerald-50 text-emerald-700',
  dismissed: 'bg-gray-100 text-gray-600',
};
